import Dexie from 'dexie';
import {
  comoConteoDeCaja,
  existenciaId,
  lugarEnElLog,
  saldoId,
  type ConteoDeDinero,
  type DineroContado,
  type JournalEntry,
  type Reader,
} from '@abastece/core';
import { DEFAULT_SETTINGS, settingsSchema, type Centavos, type Settings } from '@abastece/shared';
import { db } from './dexie.js';

export async function readSettings(): Promise<Settings> {
  return settingsDe(await db.config.toArray());
}

/** The shop's settings from the rows of `config`: each row checked on its own, defaults for the rest. */
export function settingsDe(rows: readonly { clave: string; valor: unknown }[]): Settings {
  const shape = settingsSchema.shape;
  const overrides: Record<string, unknown> = {};

  for (const row of rows) {
    if (!Object.prototype.hasOwnProperty.call(shape, row.clave)) continue;
    const parsed = shape[row.clave as keyof typeof shape].safeParse(row.valor);
    if (parsed.success) overrides[row.clave] = parsed.data;
  }

  const merged = settingsSchema.safeParse(overrides);
  return merged.success ? merged.data : DEFAULT_SETTINGS;
}

/** The same Reader contract the server implements over Postgres. Same reducers run on both. */
export const localReader: Reader = {
  usuario: (id) => db.usuarios.get(id),
  hayUsuarios: async () => (await db.usuarios.count()) > 0,
  // Added up by day (`SaldoDiario`) rather than entry by entry: the same sum from a few rows.
  saldoCuenta: async (codigo, hasta) => {
    let saldo = 0;
    await db.saldosDiarios
      .where('[cuenta+fecha]')
      .between([codigo, Dexie.minKey], [codigo, hasta ?? Dexie.maxKey], true, true)
      .each((d) => {
        saldo += d.debe - d.haber;
      });
    return saldo as Centavos;
  },
  // A dollar account's dollars, added up by day as its córdobas are (`SaldoDiario.dolares`).
  saldoCuentaDolares: async (codigo, hasta) => {
    let dolares = 0;
    await db.saldosDiarios
      .where('[cuenta+fecha]')
      .between([codigo, Dexie.minKey], [codigo, hasta ?? Dexie.maxKey], true, true)
      .each((d) => {
        dolares += d.dolares ?? 0;
      });
    return dolares as Centavos;
  },
  usuarioPorNombre: (nombre) => db.usuarios.where('nombre').equals(nombre).first(),
  dispositivo: (id) => db.dispositivos.get(id),
  dispositivoPorCodigo: (codigo) => db.dispositivos.where('codigo').equals(codigo).first(),
  config: readSettings,

  producto: (id) => db.productos.get(id),
  unidadVenta: (id) => db.unidades.get(id),
  unidadesDeProducto: (productoId) => db.unidades.where('productoId').equals(productoId).toArray(),
  listaPrecio: (id) => db.listas.get(id),
  proveedor: (id) => db.proveedores.get(id),
  existencia: (productoId, ubicacion) => db.existencias.get(existenciaId(productoId, ubicacion)),
  compra: (id) => db.compras.get(id),
  sesionCaja: (id) => db.sesiones.get(id),
  sesionAbiertaDe: (dispositivoId) =>
    db.sesiones.filter((s) => s.dispositivoId === dispositivoId && s.estado === 'abierta').first(),
  venta: (id) => db.ventas.get(id),
  // Rare, as the next: read through.
  comprasDeProducto: async (productoId, ubicacion) =>
    (await db.compras.filter((c) => c.ubicacion === ubicacion && c.lineas.some((l) => l.productoId === productoId)).toArray()).sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
    ),
  // Rare (a purchase's quantity corrected): the days from it on, read through.
  ventasDeProducto: async (productoId, ubicacion, desde) =>
    (
      await db.ventas
        .where('fecha')
        .aboveOrEqual(desde)
        .filter((v) => v.ubicacion === ubicacion && v.lineas.some((l) => l.productoId === productoId))
        .toArray()
    ).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
  saldoTelefonico: (operadora) => db.saldos.get(saldoId(operadora)),
  recarga: (id) => db.recargas.get(id),
  lotesDe: (productoId, ubicacion) =>
    db.lotes.filter((l) => l.productoId === productoId && l.ubicacion === ubicacion).toArray(),
  existenciasDe: (ubicacion) => db.existencias.where('ubicacion').equals(ubicacion).toArray(),
  existenciasDeProducto: (productoId) => db.existencias.where('productoId').equals(productoId).toArray(),

  cliente: (id) => db.clientes.get(id),
  pedido: (id) => db.pedidos.get(id),
  pedidosPendientes: () => db.pedidos.where('estado').equals('pendiente').toArray(),
  jornada: (id) => db.jornadas.get(id),
  jornadaAbiertaDe: (camion) =>
    db.jornadas.filter((j) => j.camion === camion && j.estado === 'abierta').first(),
  gasto: (id) => db.gastos.get(id),
  movimientoDueno: (id) => db.movimientosDueno.get(id),

  empleado: (id) => db.empleados.get(id),
  empleadosActivos: () => db.empleados.filter((e) => e.activo).toArray(),
  planilla: (id) => db.planillas.get(id),
  planillasDePeriodos: (periodos) =>
    db.planillas.filter((p) => periodos.includes(p.periodo) && !p.esLiquidacion).toArray(),
  pagoCotizaciones: (id) => db.pagosCotizaciones.get(id),
  pagoAguinaldo: (id) => db.pagosAguinaldo.get(id),
  oferta: (id) => db.ofertas.get(id),
  // By the product's index, which Dexie gives in primary key order within it, as the server does.
  ofertasDeProducto: (productoId) => db.ofertas.where('productoId').equals(productoId).toArray(),

  abono: (id) => db.abonos.get(id),
  incobrable: (id) => db.incobrables.get(id),
  deposito: (id) => db.depositos.get(id),
  transferenciaBanco: (id) => db.transferenciasBanco.get(id),
  devolucion: (id) => db.devoluciones.get(id),
  incobrablesDe: async (clienteId) => (await db.incobrables.where('clienteId').equals(clienteId).toArray()).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
  // In primary key order, as every other list here: the same answer on every device.
  devolucionesDeVenta: async (ventaId) => (await db.devoluciones.where('ventaId').equals(ventaId).toArray()).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
  devolucionCompra: (id) => db.devolucionesCompra.get(id),
  asientosDeEvento: async (eventoId) => (await db.asientos.where('eventId').equals(eventoId).toArray()) as JournalEntry[],
  // Few rows, and asked only of a shortage explained: no index of its own.
  gastosDeFaltante: (eventoId) => db.gastos.filter((g) => g.faltanteDe === eventoId).toArray(),
  eventoDelLog: async (eventoId) => {
    const e = await db.eventos.get(eventoId);
    return e ? { tipo: e.type, clientTs: e.clientTs, lugar: lugarEnElLog(e), payload: e.payload } : null;
  },
  conteoDeCaja: async (eventoId) => {
    const e = await db.eventos.get(eventoId);
    return e ? comoConteoDeCaja(e) : null;
  },
  // Few rows: no index of its own. The lowest id first, as every other list here.
  relevoDe: async (sesionId) =>
    (await db.sesiones.filter((s) => s.relevoDe === sesionId).toArray()).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))[0],
  // The log's counts of money, those the server stored and the books took (`sinEfecto`).
  primerConteoEntre: async (de, desde, hasta) => {
    const [eventos, refusados, jornadas] = await Promise.all([
      db.eventos.where('type').anyOf(TIPOS_DE_CONTEO).filter((e) => e.estado !== 'rechazado').toArray(),
      db.sinEfecto.toArray(),
      'general' in de ? [] : db.jornadas.filter((j) => j.sesionId === de.sesionId).toArray(),
    ]);
    const fuera = new Set(refusados.map((r) => r.eventId));
    const enOrden = eventos.map((e) => ({ id: e.id, type: e.type, payload: e.payload, lugar: lugarEnElLog(e) }));
    return primerConteo(enOrden, (id) => fuera.has(id), new Set(jornadas.map((j) => j.id)), de, desde, hasta);
  },
};

/** The kinds of event that count money: general cash's, and a till's drawer's (`Reader.primerConteoEntre`). */
export const TIPOS_DE_CONTEO = ['efectivo.arqueo', 'caja.entregada', 'caja.cerrada', 'jornada.liquidada'];

/**
 * Of the log's counts of money, the first the books took of `de` between two places, both left
 * out: what `Reader.primerConteoEntre` answers, the same from the database and from a replay in
 * memory. `rutas`: the jornadas whose settlement counts this till.
 */
export function primerConteo(
  eventos: readonly { id: string; type: string; payload: unknown; lugar: number }[],
  refusado: (id: string) => boolean,
  rutas: ReadonlySet<string>,
  de: DineroContado,
  desde: number,
  hasta: number,
): ConteoDeDinero | null {
  const delGeneral = 'general' in de;
  const suyos = eventos
    .filter((e) => !refusado(e.id))
    .filter((e) => {
      const p = (e.payload ?? {}) as { sesionId?: unknown; jornadaId?: unknown };
      if (delGeneral) return e.type === 'efectivo.arqueo';
      if (e.type === 'jornada.liquidada') return typeof p.jornadaId === 'string' && rutas.has(p.jornadaId);
      return (e.type === 'caja.entregada' || e.type === 'caja.cerrada') && p.sesionId === de.sesionId;
    })
    .sort((a, b) => a.lugar - b.lugar);
  const i = suyos.findIndex((e) => desde < e.lugar && e.lugar < hasta);
  if (i < 0) return null;
  const e = suyos[i]!;
  // General cash's first count, sent as the first: where the safe starts.
  return { id: e.id, lugar: e.lugar, inicial: delGeneral && i === 0 && (e.payload as { inicial?: unknown }).inicial === true };
}
